import React, {
  useReducer,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useDataApi } from '@adsk/alloy-helpers';
import {
  useCurrentAccountId,
  useUser,
  useAlloyFeatureFlag,
} from '@adsk/alloy-provider';
import {
  withAdminFFs,
  useAdminFeatureFlag,
} from '@common/AdminLaunchDarklyProvider';
import {
  ACCESS_LEVELS,
  COMPONENT_VARIANTS,
  OP_STATUSES,
  TIME_ZONES,
  TRACK_EVENTS,
} from '@common/constants';
import { useGetProjectTypes, useAnalytics } from '@common/hooks';
import { FLAG_NAMES } from '@common/flagNames';
import CreateProjectModalView from './component';
import { initialState, reducer, ACTIONS } from './state';
import {
  buildAccountsRequest,
  buildAddUserToProjectRequest,
  buildCreateProjectRequest,
  buildGetProjectUserRequest,
  buildTemplatesRequest,
  dataEntered,
  getAdminUserAccess,
  getProjectAdminConsoleTeamPageUrl,
  getProjectData,
  isDuplicateNameError,
  validProjectValue,
} from './utils';
import { usePublishedTemplates } from './hooks';

const CreateProjectModal = ({
  mapBoxAccessToken,
  onClose,
  onSubmit,
  show,
  variant,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const currentAccountId = useCurrentAccountId();
  const userData = useUser();
  const currentUserEmail = userData?.user?.email;

  const {
    ADMIN_FLAGS: {
      ACC_REPLACE_WRITE_CONSTRUCTION_APIS,
      ENABLE_PROJECT_ACCESS_LEVELS,
    },
    WPLT_FLAGS: {
      ACC_PROJECT_PROFILE_ADDITIONAL_FIELDS,
      CREATE_PROJECT_REDIRECT_PA_CONSOLE,
    },
  } = FLAG_NAMES;

  // Load Admin Feature Flags
  const {
    loading: loadingUseWriteConstructionApis,
    value: useWriteConstructionApis,
  } = useAdminFeatureFlag(ACC_REPLACE_WRITE_CONSTRUCTION_APIS);
  const {
    loading: loadingEnableProjectAccessLevels,
    value: enableProjectAccessLevels,
  } = useAdminFeatureFlag(ENABLE_PROJECT_ACCESS_LEVELS);

  // Load Web Platform Feature Flags
  const {
    loading: loadingAdditionalFieldsActivated,
    value: additionalFieldsActivated,
  } = useAlloyFeatureFlag(ACC_PROJECT_PROFILE_ADDITIONAL_FIELDS);
  const {
    loading: loadingPaConsoleRedirectActivated,
    value: paConsoleRedirectActivated,
  } = useAlloyFeatureFlag(CREATE_PROJECT_REDIRECT_PA_CONSOLE);

  const areFFsLoading =
    loadingAdditionalFieldsActivated ||
    loadingPaConsoleRedirectActivated ||
    loadingUseWriteConstructionApis ||
    loadingEnableProjectAccessLevels;

  const getAccountsRequest = useMemo(() => buildAccountsRequest(), []);
  // Fetch Accounts
  const { data: accountsData, loading: isFetchingAccounts } = useDataApi(
    ...getAccountsRequest
  );
  const {
    data: templatesData,
    loading: isFetchingTemplates,
    doFetch: doFetchTemplates,
  } = useDataApi();
  // Create Project
  const {
    data: createProjectData,
    error: createProjectError,
    doFetch: doCreateProjectFetch,
  } = useDataApi();
  // Assign current user as Project Admin
  const {
    data: addUserData,
    error: addUserError,
    doFetch: doAddUserFetch,
  } = useDataApi();
  const {
    data: projectUserRequestData,
    error: projectUserRequestError,
    doFetch: doProjectUserFetch,
  } = useDataApi();
  const { loading: loadingPublishedTemplates, templates: publishedTemplates } =
    usePublishedTemplates(state.selectedAccount);

  const filterAccountsByAccessLevels = (accounts) => {
    return accounts.filter((account) => {
      const isProjectAdmin = account.accessLevels.projectAdmin;
      const isAccountAdmin = account.accessLevels.accountAdmin;
      const hasAccountAdminAccessLevel =
        account.createProjectAccessLevels?.includes(
          ACCESS_LEVELS.ACCOUNT_ADMIN
        );
      const hasProjectAdminAccessLevel =
        account.createProjectAccessLevels?.includes(
          ACCESS_LEVELS.PROJECT_ADMIN
        );

      return (
        (isAccountAdmin && hasAccountAdminAccessLevel) ||
        (isProjectAdmin && hasProjectAdminAccessLevel)
      );
    });
  };

  const accounts = useMemo(() => {
    if (!accountsData) {
      return [];
    }

    let filteredAccountsData = accountsData.results.filter(
      (account) =>
        (account.accessLevels.accountAdmin &&
          (account.hasAcsUnifiedProjects || account.isAccEligible)) ||
        (variant === COMPONENT_VARIANTS.PROJECT &&
          (account.accessLevels.acsProjectAdmin ||
            (account.accessLevels.projectAdmin && account.isAccEligible)))
    );

    if (enableProjectAccessLevels) {
      filteredAccountsData = filterAccountsByAccessLevels(filteredAccountsData);
    }

    return filteredAccountsData;
  }, [accountsData, variant, enableProjectAccessLevels]);

  const templates = useMemo(() => {
    // Although unlikely, there could be a published template that also has the user as a member.
    // In that case we want to show it as an account published template.
    const publishedTemplatesIds = publishedTemplates.map(({ id }) => id);
    return !loadingPublishedTemplates && templatesData
      ? publishedTemplates.concat(
          templatesData.results.filter(
            ({ id }) => !publishedTemplatesIds.includes(id)
          )
        )
      : [];
  }, [templatesData, loadingPublishedTemplates, publishedTemplates]);

  const { projectTypes, isFetchingProjectTypes } = useGetProjectTypes(
    state.selectedAccount?.id
  );

  const latestOnSubmit = useRef(onSubmit);
  const { trackEvent } = useAnalytics();

  const sendAnalytics = (type) => {
    trackEvent(
      `${TRACK_EVENTS.SCOPE.PROJECT_ADMIN}.${TRACK_EVENTS.SOURCE.CREATE_PROJECT}.${type}`,
      {
        template_selected: !!state.selectedTemplate,
        product_id:
          variant === COMPONENT_VARIANTS.ACCOUNT
            ? 'account_admin'
            : 'project_list',
        address:
          state.autocompletedAddress !== '' ||
          Object.values(state.address).some((e) => e !== ''),
      }
    );
  };

  const resetInputState = useCallback(() => {
    dispatch({ type: ACTIONS.RESET_INPUT_STATE });
  }, []);

  const handleProjectCreated = () => {
    latestOnSubmit.current({
      status: OP_STATUSES.SUCCESS,
      response: createProjectData,
      metadata: { templateId: state.selectedTemplate },
    });
    resetInputState();
    dispatch({ type: ACTIONS.SET_IS_SAVING, payload: false });
  };

  // Handle Create project
  const handleSubmitCreateProject = () => {
    dispatch({ type: ACTIONS.SET_IS_SAVING, payload: true });
    dispatch({
      type: ACTIONS.SET_SHOW_DUPLICATED_NAME_ERROR,
      payload: false,
    });
    const projectData = getProjectData(
      state,
      additionalFieldsActivated,
      useWriteConstructionApis
    );
    doCreateProjectFetch(
      ...buildCreateProjectRequest({
        accountId: state.selectedAccount.id,
        projectData,
        useConstructionApi: useWriteConstructionApis,
      })
    );
  };

  const setMapboxTokenActive = useCallback(
    (active) =>
      dispatch({
        type: ACTIONS.SET_MAPBOX_TOKEN_ACTIVE,
        payload: active,
      }),
    []
  );

  const handleProjectNameChange = (event) => {
    dispatch({
      type: ACTIONS.SET_PROJECT_NAME,
      payload: event.target.value,
    });

    if (state.showDuplicateNameError) {
      dispatch({
        type: ACTIONS.SET_SHOW_DUPLICATED_NAME_ERROR,
        payload: false,
      });
    }
  };

  const handleProjectNameBlur = (event) => {
    const projectName = event.target.value;

    if (/^\s|\s$/.test(projectName)) {
      dispatch({
        type: ACTIONS.SET_PROJECT_NAME,
        payload: projectName.trim(),
      });
    }
  };

  const handleAccountChange = (option) => {
    dispatch({ type: ACTIONS.RESET_TEMPLATE_AND_PROJECT_TYPE });

    if (state.showDuplicateNameError) {
      dispatch({
        type: ACTIONS.SET_SHOW_DUPLICATED_NAME_ERROR,
        payload: false,
      });
    }
    dispatch({
      type: ACTIONS.SET_SELECTED_ACCOUNT,
      payload: option,
    });
  };

  const handleAddressInputModeChange = useCallback(
    (inputMode, resetAddress = true) => {
      dispatch({
        type: ACTIONS.SET_ADDRESS_INPUT_MODE,
        payload: inputMode,
      });

      if (resetAddress) {
        dispatch({ type: ACTIONS.RESET_ADDRESS });
      }
    },
    []
  );

  const handleAutocompletedAddressChange = (autocompletedAddress) => {
    dispatch({
      type: ACTIONS.SET_AUTOCOMPLETED_ADDRESS,
      payload: autocompletedAddress,
    });
  };

  const handleAddressChange = (address) => {
    const newAddress = {
      ...state.address,
      ...address,
    };
    dispatch({ type: ACTIONS.SET_ADDRESS, payload: newAddress });
  };

  const handleStartDateChange = useCallback((event) => {
    dispatch({ type: ACTIONS.SET_START_DATE, payload: event });
  }, []);

  const handleEndDateChange = useCallback((event) => {
    dispatch({ type: ACTIONS.SET_END_DATE, payload: event });
  }, []);

  const handleCancelClick = useCallback(() => {
    if (
      dataEntered(
        initialState,
        state,
        accounts.length,
        additionalFieldsActivated,
        variant
      )
    ) {
      dispatch({
        type: ACTIONS.SET_SHOW_MODAL_CONFIRMATION,
        payload: true,
      });
    } else {
      resetInputState();
      onClose();
    }
  }, [
    accounts,
    onClose,
    resetInputState,
    state,
    additionalFieldsActivated,
    variant,
  ]);

  const handleModalCloseClick = () => handleCancelClick();

  const handleModalConfirmationConfirm = () => {
    sendAnalytics(TRACK_EVENTS.NAME.CANCEL);
    dispatch({
      type: ACTIONS.SET_SHOW_MODAL_CONFIRMATION,
      payload: false,
    });
    resetInputState();
    onClose();
  };

  const handleModalConfirmationCancel = () => {
    dispatch({
      type: ACTIONS.SET_SHOW_MODAL_CONFIRMATION,
      payload: false,
    });
  };

  const handleProjectValueChange = useCallback((event) => {
    if (validProjectValue(event.target.value)) {
      dispatch({
        type: ACTIONS.SET_PROJECT_VALUE,
        payload: event.target.value,
      });
    } else {
      dispatch({
        type: ACTIONS.SET_SHOW_INVALID_PROJECT_VALUE_ERROR,
      });
    }
  }, []);

  const handleJobNumberChange = (event) =>
    dispatch({
      type: ACTIONS.SET_JOB_NUMBER,
      payload: event.target.value,
    });

  const handleProjectTypeChange = (option) =>
    dispatch({
      type: ACTIONS.SET_SELECTED_PROJECT_TYPE,
      payload: option,
    });

  const handleTemplateChange = (option) =>
    dispatch({
      type: ACTIONS.SET_SELECTED_TEMPLATE,
      payload: option,
    });

  const handleTimeZoneChange = (option) =>
    dispatch({
      type: ACTIONS.SET_TIME_ZONE,
      payload: option,
    });

  const handleCurrencyChange = (option) =>
    dispatch({
      type: ACTIONS.SET_CURRENCY,
      payload: option,
    });

  useEffect(() => {
    latestOnSubmit.current = onSubmit;
  }, [onSubmit]);

  // Set default value for the account select box
  useEffect(() => {
    if (!accountsData || !currentAccountId || state.selectedAccount) return;

    if (variant === COMPONENT_VARIANTS.ACCOUNT) {
      const defaultAccount = accountsData.results.find(
        (account) => account.id === currentAccountId
      );
      dispatch({
        type: ACTIONS.SET_SELECTED_ACCOUNT,
        payload: defaultAccount,
      });
    } else if (accountsData.results.length === 1) {
      // When the user only has access to one organization
      dispatch({
        type: ACTIONS.SET_SELECTED_ACCOUNT,
        payload: accountsData.results[0],
      });
    }
  }, [accountsData, currentAccountId, variant, state.selectedAccount]);

  // When selected account changes, fetch project types, templates and services
  useEffect(() => {
    if (!state.selectedAccount) return;

    const { id: accountId } = state.selectedAccount;
    doFetchTemplates(
      buildTemplatesRequest(accountId, useWriteConstructionApis)
    );
  }, [state.selectedAccount, doFetchTemplates, useWriteConstructionApis]);

  // Validate required fields
  useEffect(() => {
    const emptyField =
      !state.selectedProjectType ||
      !state.selectedAccount ||
      !state.projectName;

    if (emptyField !== state.missingRequiredField) {
      dispatch({
        type: ACTIONS.SET_MISSING_REQUIRED_FIELD,
        payload: emptyField,
      });
    }
  }, [
    state.missingRequiredField,
    state.projectName,
    state.selectedAccount,
    state.selectedProjectType,
  ]);

  // Set the project's time zone to the user's time zone
  useEffect(() => {
    if (show && additionalFieldsActivated) {
      const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (currentTimeZone && TIME_ZONES.includes(currentTimeZone)) {
        dispatch({
          type: ACTIONS.SET_TIME_ZONE,
          payload: { value: currentTimeZone },
        });
      }
    }
  }, [show, additionalFieldsActivated]);

  // Assign user success
  useEffect(() => {
    if (addUserData === null || createProjectData === null) return;

    sendAnalytics(TRACK_EVENTS.NAME.COMPLETE);

    if (paConsoleRedirectActivated) {
      doProjectUserFetch(
        buildGetProjectUserRequest(
          createProjectData.id,
          addUserData.id,
          useWriteConstructionApis
        )
      );
    } else {
      handleProjectCreated();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUserData]);

  // Assign user error
  useEffect(() => {
    if (addUserError === null) return;

    dispatch({ type: ACTIONS.SET_IS_SAVING, payload: false });
    latestOnSubmit.current({
      status: OP_STATUSES.ERROR,
      response: addUserError,
    });
  }, [addUserError]);

  // Create Project Success
  useEffect(() => {
    if (createProjectData === null) return;

    const { id: projectId, services } = createProjectData;
    const projectUserData = {
      email: currentUserEmail,
      [useWriteConstructionApis ? 'products' : 'services']: getAdminUserAccess(
        useWriteConstructionApis,
        services
      ),
    };

    doAddUserFetch(
      ...buildAddUserToProjectRequest({
        projectId,
        userData: projectUserData,
        useConstructionApi: useWriteConstructionApis,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createProjectData]);

  // Create Project Error
  useEffect(() => {
    if (createProjectError === null) return;

    dispatch({ type: ACTIONS.SET_IS_SAVING, payload: false });
    // Intercept and display the duplicate name field error
    if (
      createProjectError.errors &&
      isDuplicateNameError(createProjectError.errors)
    ) {
      dispatch({
        type: ACTIONS.SET_SHOW_DUPLICATED_NAME_ERROR,
        payload: true,
      });
    } else {
      // return other errors
      latestOnSubmit.current({
        status: OP_STATUSES.ERROR,
        response: createProjectError,
      });
    }
  }, [createProjectError]);

  useEffect(() => {
    if (!projectUserRequestData) return;

    handleProjectCreated();
    window.location.assign(
      getProjectAdminConsoleTeamPageUrl(createProjectData.id)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUserRequestData]);

  useEffect(() => {
    if (!projectUserRequestError) return;

    setTimeout(() => {
      doProjectUserFetch(
        buildGetProjectUserRequest(
          createProjectData.id,
          addUserData.id,
          useWriteConstructionApis
        )
      );
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUserRequestError]);

  if (areFFsLoading) {
    return null;
  }

  return (
    <CreateProjectModalView
      {...state}
      accounts={accounts}
      additionalFieldsActivated={additionalFieldsActivated}
      isFetchingAccounts={isFetchingAccounts}
      isFetchingProjectTypes={isFetchingProjectTypes}
      isFetchingTemplates={isFetchingTemplates || loadingPublishedTemplates}
      mapBoxAccessToken={mapBoxAccessToken}
      onBlurProjectName={handleProjectNameBlur}
      onChangeAccount={handleAccountChange}
      onChangeAddress={handleAddressChange}
      onChangeAddressInputMode={handleAddressInputModeChange}
      onChangeAutocompletedAddress={handleAutocompletedAddressChange}
      onChangeCurrency={handleCurrencyChange}
      onChangeEndDate={handleEndDateChange}
      onChangeJobNumber={handleJobNumberChange}
      onChangeProjectName={handleProjectNameChange}
      onChangeProjectType={handleProjectTypeChange}
      onChangeProjectValue={handleProjectValueChange}
      onChangeStartDate={handleStartDateChange}
      onChangeTemplate={handleTemplateChange}
      onChangeTimeZone={handleTimeZoneChange}
      onClickCancel={handleCancelClick}
      onCloseModal={handleModalCloseClick}
      onModalConfirmationCancel={handleModalConfirmationCancel}
      onModalConfirmationConfirm={handleModalConfirmationConfirm}
      onSubmitCreateProject={handleSubmitCreateProject}
      projectTypes={projectTypes}
      setMapboxTokenActive={setMapboxTokenActive}
      show={show}
      templates={templates}
    />
  );
};

CreateProjectModal.propTypes = {
  /** Required Access token for the Mapbox address component.
   * If not specified, defaults to manual input flow */
  mapBoxAccessToken: PropTypes.string.isRequired,
  /**  handler executed when the user clicks on the Cancel button or on the Close Dialog cross */
  onClose: PropTypes.func.isRequired,
  /** handler executed when the cuser clicks on the Create Project button */
  onSubmit: PropTypes.func.isRequired,
  /** Boolean property to set the visibility of the Modal */
  show: PropTypes.bool,
  /** Indicates project level or account level create project */
  variant: PropTypes.oneOf(Object.values(COMPONENT_VARIANTS)),
};

CreateProjectModal.defaultProps = {
  show: false,
  variant: COMPONENT_VARIANTS.PROJECT,
};

const CreateProjectModalWithAdminFFs = withAdminFFs(CreateProjectModal);

CreateProjectModalWithAdminFFs.opStatuses = OP_STATUSES;

export default CreateProjectModalWithAdminFFs;
